import * as Sentry from '@sentry/react';
import axios, { type AxiosRequestConfig, AxiosHeaders } from 'axios';
import RequestError from 'errors/RequestError';

export enum FetchMethodType {
  POST = 'POST',
  PUT = 'PUT',
  GET = 'GET',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

//TODO: fix any in this file
/* eslint-disable @typescript-eslint/no-explicit-any */
export interface IFetchOptions {
  headers?: Headers;
  resetDefaultHeaders?: boolean;
  method?: FetchMethodType;
  body?: any;
}

const getBasicHeaders = () => {
  const headers = new AxiosHeaders();

  headers.set('Accept', 'application/json');
  headers.set('Content-Type', 'application/json');

  return headers;
};

export default class Api {
  protected async fetch<Body>(url: string, options?: IFetchOptions): Promise<Body> {
    const {
      headers: customHeaders,
      method = FetchMethodType.GET,
      body,
      resetDefaultHeaders,
    } = options || {};

    const headers = resetDefaultHeaders ? new AxiosHeaders() : getBasicHeaders();

    // Do not set JSON headers if body is FormData
    if (!(body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    if (customHeaders) {
      customHeaders.forEach((value: string, header: string) => {
        headers.set(header, value);
      });
    }

    const config: AxiosRequestConfig = {
      method,
      url: `${import.meta.env.VITE_BACKEND_URL}/api${url}`,
      headers,
      data: body instanceof FormData ? body : JSON.stringify(body),
    };

    return axios(config)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        Sentry.captureException(error, {
          extra: {
            url: `${import.meta.env.VITE_BACKEND_URL}/api${url}`,
            method,
            headers: headers.toJSON(),
            body: body instanceof FormData ? body : JSON.stringify(body),
            code: error.code,
          },
        });

        this.handleError(error);
      });
  }

  private handleError(error: any): Error {
    if (error.response) {
      const { data, status, statusText } = error.response;
      const message = this.getErrorMessage(data, statusText);
      const code = data.code;
      const name = data.name || statusText;
      const stack = data.stack;
      const requestDetails = data.requestDetails;

      throw new RequestError(message, status, name, stack, code, requestDetails);
    }

    throw error;
  }

  private getErrorMessage(body: Record<string, any>, statusText: string) {
    if (body.message) {
      return body.message;
    }

    if (!body.data) {
      return body.error?.message || statusText;
    }

    return typeof body.data.error === 'string' ? body.data.error : body.data.error.message;
  }
}
